import type { DailyStatusTicket } from '../../types/workspace';
import { canRowStartTimer } from '../timer/timerModel';

/**
 * FU-44 — who sees what on Home, from the IDENTITY the server resolves
 * (`GET /permissions/me`: isOteEmployee = an employee profile; the General
 * Manager capability), not from the JWT role claim. The claim is only the
 * fallback until the server answers (or on an older server without it).
 *   * isStaff   — an OTE employee (whatever the claim: an OTE manager may
 *                 hold a customer_manager claim);
 *   * isAdmin   — the OTE manager (General Manager);
 *   * isManager — a customer-side manager login (not an employee).
 */
export function homeRoleFrom(input: {
  role: string | null | undefined;
  isOteEmployee: boolean | undefined;
  isGeneralManager: boolean;
  serverAnswered: boolean;
  /** P-33c (D-185): internal-work-only employee — a plain employee, never the manager. */
  internalOnly?: boolean;
}): { isStaff: boolean; isAdmin: boolean; isManager: boolean; internalOnly: boolean } {
  const claimStaff = input.role === 'admin' || input.role === 'agent';
  const isStaff = input.isOteEmployee ?? claimStaff;
  const isAdmin = input.serverAnswered ? input.isGeneralManager : input.role === 'admin';
  const internalOnly = isStaff && input.internalOnly === true;
  return { isStaff, isAdmin: isStaff && isAdmin && !internalOnly, isManager: !isStaff && input.role === 'customer_manager', internalOnly };
}

/**
 * D-332 - the timer that RUNS on a task ("work", not a reply), or null. A task row
 * with one shows pause + finish instead of the start triangle. `timers` is the shared
 * `['timer-status']` list (so a timer started from Claude or the extension shows too).
 */
export function runningWorkTimerFor<T extends { status: string; kind?: string | null; ticket_id: string }>(timers: readonly T[] | null | undefined, taskId: string): T | null {
  return (timers ?? []).find((tm) => tm.status === 'running' && tm.kind !== 'reply' && tm.ticket_id === taskId) ?? null;
}

/**
 * QA-4b ה' — may a "המיקוד שלי" row show ▶? The rule of every other ▶
 * (canRowStartTimer → canStartTimerOn: the user is on it, it is open and NOT
 * cancelled — the server refuses a cancelled task with 409). An older server
 * that does not send who is on the task: only the cancellation / completion
 * is checked (the list is the user's own work).
 */
export function focusCanStartTimer(task: Pick<DailyStatusTicket, 'task_type' | 'status' | 'cancelled_at' | 'assigned_agent_id' | 'agents'>, userId: string | null | undefined): boolean {
  if (task.cancelled_at || task.status === 'completed') return false;
  if (task.assigned_agent_id === undefined && task.agents == null) return !!userId;
  return canRowStartTimer(task, userId);
}

/**
 * Pure helpers for Home "היום שלי" (PRD §6.1). No React, no requests, so the
 * grouping and the state machine can be unit-tested and reused.
 */

/** YYYY-MM-DD in local time. */
export function localDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** The calendar day of a server date ("2026-04-18" or "2026-04-18T00:00:00.000Z"). */
export function dayKeyOf(value: string | null | undefined): string | null {
  if (!value) return null;
  const s = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null;
}

export type FocusSectionKey = 'overdue' | 'today' | 'inProgress' | 'rest';

export interface FocusSection {
  key: FocusSectionKey;
  items: DailyStatusTicket[];
}

const IN_PROGRESS = new Set(['in_progress', 'in_review', 'testing']);

/**
 * Splits the open work into the focus sections, keeping the server order
 * (priority, due date, staleness) inside each one:
 * באיחור (due before today) · להיום (due today) · בתהליך (being worked on) ·
 * the rest (planned, no date or a later date).
 */
export function groupFocus(items: DailyStatusTicket[], todayKey: string): FocusSection[] {
  const groups: Record<FocusSectionKey, DailyStatusTicket[]> = { overdue: [], today: [], inProgress: [], rest: [] };
  for (const item of items) {
    const due = dayKeyOf(item.due_date);
    if (due && due < todayKey) groups.overdue.push(item);
    else if (due && due === todayKey) groups.today.push(item);
    else if (IN_PROGRESS.has(item.status)) groups.inProgress.push(item);
    else groups.rest.push(item);
  }
  return (['overdue', 'today', 'inProgress', 'rest'] as FocusSectionKey[]).map((key) => ({ key, items: groups[key] }));
}

/** The task the header's "התחלת עבודה" starts: the first one in focus order. */
export function nextTask(sections: FocusSection[]): DailyStatusTicket | null {
  for (const s of sections) if (s.items.length) return s.items[0];
  return null;
}

export type AttendanceState = 'clocked_in' | 'on_break' | 'clocked_out';

export type HeaderMode =
  | 'loading'
  | 'timer'
  | 'startNext'
  | 'takeUnassigned'
  | 'replyOldest'
  | 'none';

/**
 * The page's ONE primary action, chosen by state (PRD §6.1, principle 4).
 * Work only (owner, 2026-10-11): clocking in, breaks and clocking out live in
 * the top bar's attendance control, so the header never offers them.
 * A running timer always wins: the header shows it live instead of a button.
 */
export function headerMode(input: {
  /** The roles that work tasks and clock in (admin, agent). */
  attendanceRole: boolean;
  isManager: boolean;
  loading: boolean;
  timerRunning: boolean;
  hasNextTask: boolean;
  hasAwaiting: boolean;
}): HeaderMode {
  if (input.timerRunning) return 'timer';
  if (input.attendanceRole) {
    if (input.loading) return 'loading';
    return input.hasNextTask ? 'startNext' : 'takeUnassigned';
  }
  if (input.isManager) return input.hasAwaiting ? 'replyOldest' : 'none';
  return 'none';
}

/** Minutes → "3:05" (hours:minutes, LTR digits). */
export function formatHoursMinutes(totalMinutes: number): string {
  const m = Math.max(0, Math.round(totalMinutes));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

/** Elapsed since an ISO time → "01:02:09" (live timer, same format as the timer capsule). */
export function formatElapsed(sinceIso: string, now: number): string {
  const s = Math.max(0, Math.floor((now - new Date(sinceIso).getTime()) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return `${String(h).padStart(2, '0')}:${mm}:${ss}`;
}

/** Local "HH:mm" of an ISO date-time. */
export function formatClock(iso: string | null | undefined, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
}

/** Sunday-first week that contains `d` (Israeli week, PRD §4.19). */
export function weekDays(d: Date): Date[] {
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay());
  return Array.from({ length: 7 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
}
